import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { PetiteReleveCatalogue } from "./PetiteReleveCatalogue";

const DISC = { "": "Général (toutes disciplines)", muay_thai_educatif: "Kickboxing/Muaythai", kickboxing_educatif: "Kickboxing/Muaythai (ancien)", jjb: "Jiu-Jitsu Brésilien", can_ryu_demo: "Can Ryu (démo)", petits_samourais: "Petits Samouraïs (parcours)", petite_releve: "Petite relève — dès 3 ans (pilote)" };
const empty = { title: "", body_md: "", discipline: "", event_id: "", version: "2026.1", source_url: "", active: true, statut: "publie" };

export function RulesTab({ eventId }) {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`/jm/rules?event_id=${eventId}`); setRules(r.data.rules || []); } catch { toast.error("Erreur"); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const seed = async () => { await api.post("/jm/rules/seed"); toast.success("Règlements de base créés"); load(); };
  const save = async () => {
    try {
      if (form.id) { const r = await api.patch(`/jm/rules/${form.id}`, form); toast.success(r.data.new_version ? "Nouvelle version créée (l'ancienne est archivée)" : "Règlement mis à jour"); }
      else { await api.post("/jm/rules", form); toast.success("Règlement ajouté"); }
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Supprimer / archiver « ${r.title} » ?`)) return;
    const res = await api.delete(`/jm/rules/${r.id}`); toast.success(res.data.archived ? "Archivé (acceptations existantes)" : "Supprimé"); load();
  };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3" data-testid="tab-content-reglements">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-zinc-400">Case obligatoire par règlement à l'étape 8 du formulaire. Modifier un texte déjà accepté crée automatiquement une <b>nouvelle version</b> (traçabilité).</p>
        <div className="flex gap-2">
          {rules.length === 0 && <button onClick={seed} className="text-xs border border-amber-500/40 text-amber-300 rounded-lg px-3 py-1.5" data-testid="rules-seed-btn">Créer les règlements de base</button>}
          <button onClick={() => setForm({ ...empty, event_id: "" })} className="text-xs bg-amber-500 text-black font-semibold rounded-lg px-3 py-1.5" data-testid="rules-add-btn">+ Règlement</button>
        </div>
      </div>
      {form && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="rules-form">
          <div className="grid sm:grid-cols-3 gap-2">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titre *" className="sm:col-span-2 rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="rule-title" />
            <input value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="Version" className="rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="rule-version" />
            <select value={form.discipline} onChange={(e) => set("discipline", e.target.value)} className="rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="rule-discipline">
              {Object.entries(DISC).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={form.event_id} onChange={(e) => set("event_id", e.target.value)} className="rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="rule-scope">
              <option value="">Tous les événements</option><option value={eventId}>Cet événement seulement</option>
            </select>
            <input value={form.source_url} onChange={(e) => set("source_url", e.target.value)} placeholder="Source officielle (URL)" className="rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="rule-source" />
          </div>
          <textarea value={form.body_md} onChange={(e) => set("body_md", e.target.value)} placeholder="Texte du règlement *" className="w-full min-h-[120px] rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="rule-body" />
          <label className="flex items-center gap-2 text-xs text-amber-200"><input type="checkbox" checked={form.statut === "brouillon"} onChange={(e) => set("statut", e.target.checked ? "brouillon" : "publie")} data-testid="rule-brouillon" /> Brouillon — document de travail non homologué (jamais montré aux inscriptions)</label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="text-xs border border-zinc-700 rounded-lg px-3 py-1.5">Annuler</button>
            <button onClick={save} disabled={!form.title || !form.body_md} className="text-xs bg-amber-500 text-black font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid="rule-save">Enregistrer</button>
          </div>
        </div>
      )}
      <div className="space-y-2" data-testid="rules-list">
        {rules.map((r) => (
          <div key={r.id} className={`rounded-xl border p-3 ${r.active ? "border-zinc-800 bg-black/30" : "border-zinc-900 opacity-50"}`} data-testid={`rule-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <b>{r.title}</b>
              <span className="text-[10px] rounded-full border border-zinc-700 px-2 py-0.5">v{r.version}</span>
              <span className="text-[10px] text-zinc-500">{DISC[r.discipline] || r.discipline} · {r.event_id ? "cet événement" : "tous"} · révisé {r.revised_at}</span>
              {!r.active && <span className="text-[10px] text-zinc-500">archivé</span>}
              {r.statut === "brouillon" && <span className="text-[10px] rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 px-2 py-0.5" data-testid={`rule-brouillon-${r.id}`}>Brouillon — non homologué</span>}
              <span className="ml-auto text-[10px] text-emerald-300">{r.acceptances} acceptation(s)</span>
              <button onClick={() => setForm({ ...empty, ...r })} className="text-xs text-amber-300" data-testid={`rule-edit-${r.id}`}>✏️</button>
              <button onClick={() => del(r)} className="text-xs text-red-300" data-testid={`rule-del-${r.id}`}>🗑️</button>
            </div>
            <p className={`text-xs text-zinc-400 mt-1 whitespace-pre-line ${r.statut === "brouillon" ? "line-clamp-6" : "line-clamp-3"}`}>{r.body_md}</p>
          </div>
        ))}
        {rules.length === 0 && <p className="text-xs text-zinc-500 italic">Aucun règlement. Clique « Créer les règlements de base » pour partir des 5 modèles KAIZEN.</p>}
      </div>
      <PetiteReleveCatalogue />
    </div>
  );
}

export function DocumentsTab({ eventId }) {
  const [data, setData] = useState({ documents: [], drafts_in_progress: 0 });
  const load = useCallback(async () => {
    try { const r = await api.get(`/jm/events/${eventId}/documents`); setData(r.data); } catch { toast.error("Erreur"); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);
  const verify = async (id, status) => {
    const note = status === "rejected" ? (window.prompt("Motif du refus (visible par l'organisateur seulement)") || "") : "";
    await api.patch(`/jm/documents/${id}/verify`, { status, note }); toast.success(status === "verified" ? "Document vérifié" : "Document refusé"); load();
  };
  const open = async (id) => {
    try {
      const r = await api.get(`/jm/documents/${id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Téléchargement impossible"); }
  };
  const badge = { uploaded: "bg-amber-500/20 text-amber-300", verified: "bg-emerald-500/20 text-emerald-300", rejected: "bg-red-500/20 text-red-300" };
  return (
    <div className="space-y-3" data-testid="tab-content-documents">
      <p className="text-xs text-zinc-400">🔒 Documents confidentiels (médical, identité, fédération). Stockage privé — jamais servi publiquement ; chaque ouverture est journalisée. {data.drafts_in_progress} brouillon(s) d'inscription en cours.</p>
      <div className="space-y-2" data-testid="documents-list">
        {data.documents.map((x) => (
          <div key={x.id} className="rounded-xl border border-zinc-800 bg-black/30 p-3 flex flex-wrap items-center gap-2 text-sm" data-testid={`doc-row-${x.id}`}>
            <b>{x.athlete_name}</b><span className="text-zinc-500 text-xs">{x.school_name}</span>
            <span className="text-xs">· {x.type} · {x.filename} ({Math.round((x.size || 0) / 1024)} Ko)</span>
            <span className={`text-[10px] rounded-full px-2 py-0.5 ${badge[x.status] || "bg-zinc-800"}`}>{x.status}</span>
            {x.verified_by && <span className="text-[10px] text-zinc-500">par {x.verified_by}</span>}
            <div className="ml-auto flex gap-1">
              <button onClick={() => open(x.id)} className="text-xs border border-zinc-700 rounded-lg px-2 py-1" data-testid={`doc-open-${x.id}`}>👁️ Ouvrir</button>
              <button onClick={() => verify(x.id, "verified")} className="text-xs bg-emerald-600/80 rounded-lg px-2 py-1" data-testid={`doc-verify-${x.id}`}>✓ Vérifié</button>
              <button onClick={() => verify(x.id, "rejected")} className="text-xs bg-red-600/80 rounded-lg px-2 py-1" data-testid={`doc-reject-${x.id}`}>✕ Refuser</button>
            </div>
          </div>
        ))}
        {data.documents.length === 0 && <p className="text-xs text-zinc-500 italic">Aucun document téléversé pour cet événement.</p>}
      </div>
    </div>
  );
}
